import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import sortIcon from "../../assets/images/sort.svg";
import styles from "./productList.module.scss";
import Button from "../../components/button/Button";
import add from "../../assets/images/add.svg";
import { useEffect, useRef, useState, useCallback } from "react";
import ActionsIcon from "../../assets/images/kebabMenu.svg";
import AvailableIcon from "../../assets/images/available.svg";
import NotAvailableIcon from "../../assets/images/notFound.svg";
import ProductPopup from "./ProductPopup";
import { useSelector, useDispatch } from "react-redux";
import {
  selectProductList,
  updatePage,
  addProduct,
  sDActions,
  getProductList,
  selectLimit,
  selectCurrentPage,
  searchProduct,
  updatePreDefinedFilterValues,
  selectTotalResults,
  setFilter,
} from "./productListSlice";
import TablePagination from "../../components/tablePagination/TablePagination";
import filterIcon from "../../assets/images/filter.svg";
import ProductListFilter from "./ProductListFilter";
import { Menu, MenuItem } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/slide.css";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import { Helmet } from "react-helmet";
import AddProductWizard from "./AddProductWizard/AddProductWizard";
import { FaEye } from 'react-icons/fa'; // View icon
import { FaEdit } from 'react-icons/fa'; // Edit icon
import { FaPlus } from 'react-icons/fa'; // Create icon
import { FaCommentDots, FaSort, FaTrash } from "react-icons/fa";
import { selectUser, selectToast } from "../../app/appSlice";
import sharedDataApi from "../../api/sharedDataApi";
import { productApi } from "../../api/productApi";
import Popup from "../../components/popUp/Popup";

const modals = Object.freeze({
  addProduct: "addProduct",
  none: null,
});

const ProductList = () => {
  const navigate = useNavigate();
  const productList = useSelector(selectProductList);
  const itemsPerPage = useSelector(selectLimit);
  const currPage = useSelector(selectCurrentPage);
  const totalResults = useSelector(selectTotalResults);
  const dispatch = useDispatch();
  const [currModal, setCurrModal] = useState(modals.none);
  const [currProduct, setCurrentProduct] = useState(null);

  const [showFilter, setShowFilter] = useState(false);
  const searchTimeoutId = useRef(null);
  const [sortProp, setSortProp] = useState(null);
  const [sortDirection, setSortDirection] = useState(true); // true = asc, false = desc
  const user = useSelector(selectUser);
  const toast = useSelector(selectToast); // get toast state
  const [activeFilter, setActiveFilter] = useState(null);
  const [searchDisabled, setSearchDisabled] = useState(false);
  const [popupConfig, setPopupConfig] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [filteredProductList, setFilteredProductList] = useState([]);

    // Filter draft products to show only to their creators
  useEffect(() => {
    const filterDraftProducts = async () => {
      if (!productList || productList.length === 0) {
        setFilteredProductList([]);
        return;
      }

      const filteredList = await Promise.all(
        productList.map(async (product) => {
          // If product is not in Draft state, show it to everyone
          if (product.State !== "Draft") {
            return product;
          }

          // For Draft products, check if current user is the creator
          try {
            const actionItem = await sharedDataApi.getActionItemById(product.id);
            
            // If actionItem exists and current user is the creator, show the product
            if (actionItem && actionItem.CreatedByEmail?.toLowerCase() === user?.email?.toLowerCase()) {
              return product;
            }
            // If no actionItem found or user is not the creator, hide the product
            return null;
          } catch (error) {
            console.warn(`Could not fetch ActionItem for product ${product.id}:`, error);
            // If we can't verify, hide the draft product for safety
            return null;
          }
        })
      );

      // Remove null values (hidden draft products)
      setFilteredProductList(filteredList.filter((product) => product !== null));
    };

    filterDraftProducts();
  }, [productList, user?.email]);

  useEffect(() => {
    dispatch(setFilter({}))
    dispatch(getProductList({ initLoad: true }));
  }, []);

  useEffect(() => {
    console.log(sortProp, "sortprop")
    if(sortProp) {
      dispatch(getProductList({ initLoad: true, sort: { direction: sortDirection ? "asc" : "desc", field: sortProp }, }));
    }
  }, [sortProp, sortDirection, dispatch]);

  const setSortObjectForProp = (prop) => {
    if (sortProp === prop) {
      setSortDirection(!sortDirection);
    } else {
      setSortDirection(true);
      setSortProp(prop);
    }
  };

  const handleSearch = useCallback((event) => {
    if (searchTimeoutId.current) {
      clearTimeout(searchTimeoutId.current);
    }
    searchTimeoutId.current = setTimeout(() => {
      const searchText = event.target.value.trim();
      const filterPayload =
        searchText === ""
          ? // when search is cleared, preserve any active filter
            activeFilter && Object.keys(activeFilter).length > 0
              ? activeFilter
              : {}
          : {
            or: [
              {
                ModelNumber: {
                  wildcard: `*${searchText}*`,
                },
              },
              {
                Family: {
                  wildcard: `*${searchText}*`,
                },
              },
            ],
          };
      const sortPayload = sortProp ? { direction: sortDirection ? "asc" : "desc", field: sortProp } : null;
      // Dispatch searchProduct with both filter and current sort (if any) so clearing search doesn't remove sorting
      dispatch(searchProduct({ filter: filterPayload, sort: sortPayload }));
    }, 1000);
  }, [dispatch, activeFilter, sortProp, sortDirection]);

  const handleActions = ({ action, item }) => {
    const link = "/products/";

    const fetchClonedProduct = async (cloneId) => {
      try {
        const response = await productApi.searchProducts({
          filter: { id: { eq: cloneId } },
          sort: { direction: "desc", field: "createdAt" },
        });

        const cloned = response?.items?.[0];

        if (cloned) {
          const modelNumber = cloned.ModelNumber;
          const CloneStage = cloned.State; // Assuming 'State' means stage
          const Cloneversion =
            CloneStage === "Production"
              ? cloned.ProductionVersion
              : cloned.StagingVersion;
          const CloneIds = cloned.id;

          const navigateUrl = `${link}create-shared-data?id=${item.id}&model=${modelNumber}&stage=${CloneStage}&version=${Cloneversion}&cloneId=${CloneIds}`;
          navigate(navigateUrl);
        } else {
          console.warn("Cloned product not found, using default path.");
          navigate(`${link}create-shared-data?id=${item.id}`);
        }
      } catch (error) {
        console.error("Error fetching cloned product:", error);
        navigate(`${link}create-shared-data?id=${item.id}`);
      }
    };

    switch (action) {
      case sDActions.createSD:
        if (item.CloneFromProductId) {
          fetchClonedProduct(item.CloneFromProductId);
        } else {
          navigate(`${link}create-shared-data?id=${item.id}`);
        }
        return;
      case sDActions.updateSD:
        navigate(link + `update-shared-data?id=${item.id}&actionId=${item.ModelNumber}`);
        return;
      case sDActions.viewSD:
        navigate(link + `view-shared-data?id=${item.id}`);
        return;
      default:
        return;
    }
  };

  const sortArrayByObject = (array) => {
    if (!sortProp) return array;
    const copyArray = [...array];
    copyArray.sort(function (a, b) {
      const [x, y] = [a[sortProp] || "", b[sortProp] || ""];
      if (isNaN(x - y))
        return sortDirection
          ? ("" + x).localeCompare(y)
          : ("" + y).localeCompare(x);
      else return sortDirection ? x - y : y - x;
    });
    return copyArray;
  };

  const setFilterAndQuery = (dataFilter) => {
    let aggregateValues = [];
    for (let key of Object.keys(dataFilter)) {
      aggregateValues = [...aggregateValues, ...dataFilter[key]];
    }
    if (aggregateValues.length === 0) {
      // clearing filters -> update activeFilter and clear search filter
      setActiveFilter({});
      setSortProp(null);
      setSortDirection(true);
      dispatch(searchProduct({ filter: {}, sort: null }));
      return;
    }

    const createOrCondition = (key, valArr) => {
      if (valArr.length > 0)
        return { or: valArr.map((val) => ({ [key]: { eq: val.value } })) };
      else return {};
    };
    const andArr = [];
    Object.keys(dataFilter).forEach((key) => {
      if (Object.keys(createOrCondition(key, dataFilter[key])).length > 0)
        andArr.push(createOrCondition(key, dataFilter[key]));
    });
    const filter = { and: andArr };
    // store the applied filter so subsequent clearing of search preserves it
    setActiveFilter(filter);
    const sortPayload = sortProp ? { direction: sortDirection ? "asc" : "desc", field: sortProp } : null;
    dispatch(searchProduct({ filter, sort: sortPayload }));
  };

  const onPageChange = async ({ gotoPage }) => {
    const sortPayload = sortProp ? { direction: sortDirection ? "asc" : "desc", field: sortProp } : null
    dispatch(updatePage({ gotoPage, sortPayload }));
  };

  const onItemsPerPageChange = async (limit) => {
    const sortPayload = sortProp ? { direction: sortDirection ? "asc" : "desc", field: sortProp } : null;
    await dispatch(getProductList({ initLoad: true, limit, sort: sortPayload }));
  };

  const handleDeleteProduct = (item) => {
    setDeleteItem(item);
    setPopupConfig({
      title: <b>{`Are you sure you want to delete product "${item.ModelNumber}"?`}</b>,
      successText: "Yes",
      cancelText: "No",
      onSuccess: () => confirmDeleteProduct(item),
      onCancel: () => setPopupConfig(null),
    });
  };

  const confirmDeleteProduct = async (itemToDelete) => {
  const targetItem = itemToDelete || deleteItem;
  
  if (!targetItem) {
    console.error("No item to delete");
    setPopupConfig(null);
    return;
  }
  setPopupConfig(null);
  try {
    let latestActionItem = null;

    try {
      latestActionItem = await sharedDataApi.getActionItemById(targetItem.id);
    } catch (err) {
      console.warn("No ActionItem found (safe to proceed)");
    }

    if (
      latestActionItem &&
      latestActionItem.Status === "IN_REVIEW" &&
      latestActionItem.CreatedByEmail?.toLowerCase() !== user?.email?.toLowerCase()
    ) {
      setPopupConfig({
        title: (
          <>
            {`This product has already been submitted for review by ${latestActionItem.CreatedByEmail}. Cannot delete. Please refresh the page.`}
          </>
        ),
        successText: "OK",
        cancelText: null,
        onSuccess: () => setPopupConfig(null),
        onCancel: null,
      });
      return;
    }

    await productApi.deleteProduct({
      input: {
        id: targetItem.id,
        ModelNumber: targetItem.ModelNumber,
      },
    });

    try {
      if (latestActionItem) {
        await sharedDataApi.deleteActionItem({
          input: { id: targetItem.id },
        });
      }
    } catch (actionItemError) {
      console.warn("ActionItem deletion failed or already deleted");
      // Do NOT block user — product already deleted
    }

    await dispatch(getProductList({ initLoad: true }));
    setDeleteItem(null);
    // setPopupConfig(null);

  } catch (error) {
    console.error("Delete product error:", error);
    setPopupConfig({
      title: <b>Failed to delete product. Please try again.</b>,
      successText: "OK",
      cancelText: null,
      onSuccess: () => setPopupConfig(null),
      onCancel: null,
    });
  }
  };

  const onAddProductSuccess = (newProduct) => {
    dispatch(
      addProduct({
        product: { ...newProduct },
      })
    );
    setCurrModal(modals.none);
  };
  const resetSort = useCallback(() => {
    setSortProp(null);
    setSortDirection(true);
    dispatch(getProductList({ initLoad: true, sort: null }));
  }, []);
  useEffect(() => {
    setSearchDisabled(!!toast?.isOpen && toast?.message === "No result found!");
  }, [toast]);

  const formatState = (state) => {
    if (!state) return "-";
    return state.replace(/_/g, " ");
  };
  return (
    <div className={styles.body}>
      <Helmet>
        <title>Shared Data Portal | Products</title>
      </Helmet>
      <div className={styles.pageNameContainer}>
        <div className={styles.pageName}>Products</div>
        <Breadcrumbs />
      </div>
      <div className={styles.list}>
        <div className={styles.data_filter}>
          <div className={styles.inputIcons}>
            <form className={styles.search}>
              <input
                onChange={handleSearch}
                className={styles.search}
                type="search"
                placeholder="Search model, family"
                disabled={searchDisabled}
              />
            </form>
          </div>
          <div className={styles.buttons}>
            <Button
              style={styles.filter}
              onClick={async () => {
                if (!showFilter) {
                  await dispatch(updatePreDefinedFilterValues());
                }
                setShowFilter(!showFilter);
              }}
            >
              <img
                className={styles.filterIcon}
                src={filterIcon}
                alt="filter"
              />
              <span>Filter</span>
            </Button>
            <Button
              style={styles.addProductButton}
              onClick={() => setCurrModal(modals.addProduct)}
            >
              <img className={styles.plusIcon} src={add} alt="plus"></img>
              <span>Add Product</span>
            </Button>
          </div>
        </div>
        {showFilter && <ProductListFilter setFilter={setFilterAndQuery} onResetSort={resetSort} />}
        <div className={styles.table}>
          <div className={styles.tableContainer}>
            <div style={{ width: "100%" }}>
              <div className={styles.header}>
                <div onClick={() => setSortObjectForProp("ModelNumber")}>
                  <span>Model Number</span>
                  <FaSort className={styles.icon} />
                  {sortProp === 'ModelNumber' && (
                    <span className={styles.sortIndicator}>
                      {sortDirection ? '↑' : '↓'}
                    </span>
                  )}
                </div>
                <div onClick={() => setSortObjectForProp("Family")}>
                  <span>Product Family</span>
                  <FaSort className={styles.icon} />
                  {sortProp === 'Family' && (
                    <span className={styles.sortIndicator}>
                      {sortDirection ? '↑' : '↓'}
                    </span>
                  )}
                </div>
                {/* <div onClick={() => setSortObjectForProp("Category")}>
                  <span>Product Category</span>
                  <FaSort className={styles.icon} />
                  {sortProp === 'Category' && (
                    <span className={styles.sortIndicator}>
                      {sortDirection ? '↑' : '↓'}
                    </span>
                  )}
                </div> */}
                <div onClick={() => setSortObjectForProp("SubCategory")}>
                  <span>Product sub category</span>
                  <FaSort className={styles.icon} />
                  {sortProp === 'SubCategory' && (
                    <span className={styles.sortIndicator}>
                      {sortDirection ? '↑' : '↓'}
                    </span>
                  )}
                </div>
                <div style={{ maxWidth: "0.8rem" }}>Shared Data</div>
                <div style={{ maxWidth: "0.8rem" }}>Revision No.</div>
                <div style={{ maxWidth: "0.8rem" }}>BT Enabled</div>
                <div
                  style={{ maxWidth: "0.8rem" }}
                  onClick={() => setSortObjectForProp("ControlId")}
                >
                  <span>Control ID</span>
                  <FaSort className={styles.icon} />
                  {sortProp === 'ControlId' && (
                    <span className={styles.sortIndicator}>
                      {sortDirection ? '↑' : '↓'}
                    </span>
                  )}
                </div>
                <div
                  style={{ maxWidth: "0.8rem" }}
                  onClick={() => setSortObjectForProp("ControlType")}
                >
                  <span>Control Type</span>
                  <FaSort className={styles.icon} />
                  {sortProp === 'ControlType' && (
                    <span className={styles.sortIndicator}>
                      {sortDirection ? '↑' : '↓'}
                    </span>
                  )}
                </div>
                <div>
                  <span>State</span>
                </div>
                <div style={{ maxWidth: "0.8rem" }}>Actions</div>
              </div>
              {(sortProp ? filteredProductList : sortArrayByObject(filteredProductList)).map((item) => {
                return (
                  <div key={JSON.stringify(item)} className={styles.contentRow}>
                    <div className={styles.td}>{item.ModelNumber}</div>
                    <div className={styles.td}>{item.Family}</div>
                    {/* <div className={styles.td}>{item.Category}</div> */}
                    <div className={styles.td}>{item.SubCategory}</div>
                    <div className={styles.td} style={{ maxWidth: "0.8rem" }}>
                      <div className={styles.availabilityIcon}>
                        <img
                          src={
                            item.SdAvailable ? AvailableIcon : NotAvailableIcon
                          }
                          alt="Actions Icon"
                        />
                      </div>
                    </div>
                    <div className={styles.td} style={{ maxWidth: "0.8rem" }}>
                      {item.ProductionVersion}
                    </div>
                    <div className={styles.td} style={{ maxWidth: "0.8rem" }}>
                      <div className={styles.availabilityIcon}>
                        <img
                          src={
                            item.BtEnabled ? AvailableIcon : NotAvailableIcon
                          }
                          alt="Actions Icon"
                        />
                      </div>
                    </div>
                    <div style={{ maxWidth: "0.8rem" }} className={styles.td}>
                      {item.ControlId}
                    </div>
                    <div style={{ maxWidth: "0.8rem" }} className={styles.td}>
                      {item.ControlType}
                    </div>
                    <div className={styles.td}>
                      <div className={styles.state}>{formatState(item.State)}</div>
                    </div>
                    <div style={{ maxWidth: "0.8rem" }} className={styles.td}>

                      {item.State && item.State !== "Draft" && item.State !== "In_Review" && (
                        <><FaEye className={styles.icon} title="View Shared Data" onClick={() => handleActions({
                          action: sDActions.viewSD,
                          item,
                        })} />
                          {item.State && item.State !== "Pre_Prod" &&  (
                            <>
                              {user?.roles?.includes('admin') && (
                                <FaEdit
                                  className={styles.icon}
                                  title="Edit Shared Data"
                                  onClick={() => handleActions({
                                    action: sDActions.updateSD,
                                    item,
                                  })}
                                />
                              )}
                            </>
                          )}
                        </>
                      )}
                      {(item.State === "Draft" || !item.State) && (
                        <FaTrash
                          className={styles.icon}
                          title="Delete Product"
                          onClick={() => handleDeleteProduct(item)}
                        />
                      )}
                      {item.State === "Draft" && (
                        <FaPlus
                          className={styles.icon}
                          title="Continue Draft"
                          onClick={() =>
                            handleActions({
                              action: sDActions.createSD,
                              item,
                            })
                          }
                        />
                      )}
                      {!item.State && (
                        <FaPlus
                          className={styles.icon}
                          title="Create Shared Data"
                          onClick={() =>
                            handleActions({
                              action: sDActions.createSD,
                              item,
                            })
                          }
                        />
                      )}
                      {item.State === "In_Review" && (
                        <Link to={`/reviewItem?id=${item.id}`}>
                          <FaCommentDots
                            className={styles.icon}
                            title="Review Shared Data"
                          />
                        </Link>
                      )}
                      {/* <Menu
                        menuButton={
                          <div className={styles.actionIcon}>
                            <img src={ActionsIcon} alt="Actions Icon" />
                          </div>
                        }
                        transition
                      >
                        {item.State && (
                          <>
                            <MenuItem
                              onClick={() =>
                                handleActions({
                                  action: sDActions.viewSD,
                                  item,
                                })
                              }
                            >
                              View Shared Data
                            </MenuItem>
                            <MenuItem
                              onClick={() =>
                                handleActions({
                                  action: sDActions.updateSD,
                                  item,
                                })
                              }
                            >
                              Update Shared Data
                            </MenuItem>
                          </>
                        )}
                        {!item.State && (
                          <MenuItem
                            onClick={() =>
                              handleActions({
                                action: sDActions.createSD,
                                item,
                              })
                            }
                          >
                            Create Shared Data
                          </MenuItem>
                        )}
                      </Menu> */}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <TablePagination
            total={totalResults}
            currentPage={currPage}
            onPageChange={onPageChange}
            itemsPerPage={itemsPerPage}
            items={filteredProductList}
            onItemsPerPageChange={onItemsPerPageChange}
            showTotal={false}
          />
        </div>
      </div>
      {currModal === modals.addProduct && (
        // <ProductPopup
        //   onAbort={() => setCurrModal(modals.none)}
        //   onSuccess={onAddProductSuccess}
        //   title={"Add New Product"}
        //   onSuccessButtonText={"Add"}
        // />
        <AddProductWizard onAbort={() => setCurrModal(modals.none)} />
      )}
      {popupConfig && (
        <Popup
          title={popupConfig.title}
          description={popupConfig.description || null}
          successText={popupConfig.successText}
          cancelText={popupConfig.cancelText}
          warningMessage={popupConfig.warningMessage || null}
          onSuccess={popupConfig.onSuccess}
          onCancel={popupConfig.onCancel}
        />
      )}
    </div>
  );
};

export default ProductList;
